import React from 'react';

import { Box, Typography, styled } from '@mui/material';

import { assetsURL } from '../../../config';
import { Venue } from '../../../graphql/API';
import { VENUE_MANUAL_TAGS } from '../../../utils/venue-tags';
import Button from '../../Button/Button';
import BaseDialog, { BaseDialogProps } from '../Base';
import { FlowStepper } from './components/FlowStepper';

const SkipButton = styled(Button)(() => ({
  fontSize: '14px',
  backgroundColor: '#F1F2F4',
  borderRadius: '8px',
  padding: '8px 15px',
  fontFamily: 'Poppins',
}));

const HowItWorksButton = styled(Button)(() => ({
  fontSize: '14px',
  borderRadius: '8px',
  padding: '8px 15px',
  fontWeight: 600,
  fontFamily: 'Poppins',
}));

const VideoComponent = (
  <Box display={'flex'} alignItems={'flex-start'} gap={'20px'}>
    <div style={{ borderRadius: '8px', overflow: 'hidden', width: '100%', height: 'auto' }}>
      <video
        width='100%'
        height='100%'
        controls={false}
        src={`${assetsURL}venueonboarding/onboarding-video-1.mp4`}
        autoPlay
        muted
        loop
        style={{ display: 'block' }}
      />
    </div>
    <div style={{ borderRadius: '8px', overflow: 'hidden', width: '100%', height: 'auto' }}>
      <video
        width='100%'
        height='100%'
        controls={false}
        src={`${assetsURL}venueonboarding/onboarding-video-2.mp4`}
        autoPlay
        muted
        loop
        style={{ display: 'block' }}
      />
    </div>
    <div style={{ borderRadius: '8px', overflow: 'hidden', width: '100%', height: 'auto' }}>
      <video
        width='100%'
        height='100%'
        controls={false}
        src={`${assetsURL}venueonboarding/onboarding-video-3.mp4`}
        autoPlay
        muted
        loop
        style={{ display: 'block' }}
      />
    </div>
  </Box>
);

const ONLINE_BRAND_STEPS = (brandName: string) => [
  {
    image: '',
    descriptionComponent: (
      <Box display={'flex'} flexDirection={'column'} gap={'6px'}>
        <Typography fontWeight={600} fontFamily={'Poppins'}>
          Hi {brandName}
        </Typography>
        <Typography fontSize={'14px'} fontFamily={'Poppins'}>
          Welcome to Beautypass! Start promoting your business with the world's largest community of
          fashion models
        </Typography>
      </Box>
    ),
    videoComponent: VideoComponent,
  },
  {
    image: '/png/brand-onboarding-2.png',
    descriptionComponent: (
      <Box maxWidth={'60%'} margin={'0 auto'}>
        <Typography fontSize={'16px'} fontFamily={'Poppins'} textAlign={'center'}>
          <b>List your products</b> from the dashboard page or product page
        </Typography>
      </Box>
    ),
    videoComponent: <></>,
  },
  {
    image: '/png/brand-onboarding-3.png',
    descriptionComponent: (
      <Box maxWidth={'80%'} margin={'0 auto'}>
        <Typography fontSize={'16px'} fontFamily={'Poppins'} textAlign={'center'}>
          Our members will request to promote your product. You have the option to <b>accept</b> or{' '}
          <b>decline</b>.
        </Typography>
      </Box>
    ),
    videoComponent: <></>,
  },
  {
    image: '/png/brand-onboarding-4.png',
    descriptionComponent: (
      <Typography fontSize={'16px'} fontFamily={'Poppins'} textAlign={'center'}>
        Chat with our members to <b>coordinate shipment</b>
      </Typography>
    ),
    videoComponent: <></>,
  },
  {
    image: '/png/brand-onboarding-5.png',
    descriptionComponent: (
      <Typography fontSize={'16px'} fontFamily={'Poppins'} textAlign={'center'}>
        <b>Members will post</b> and <b>promote your business</b> within 14 days
      </Typography>
    ),
    videoComponent: <></>,
  },
];

const VENUE_STEPS = (venueName: string) => [
  {
    image: '',
    descriptionComponent: (
      <Box display={'flex'} flexDirection={'column'} gap={'6px'}>
        <Typography fontWeight={600} fontFamily={'Poppins'}>
          Hi {venueName}
        </Typography>
        <Typography fontSize={'14px'} fontFamily={'Poppins'}>
          Welcome to Beautypass! Start promoting your business with the world's largest community of
          fashion models
        </Typography>
      </Box>
    ),
    videoComponent: VideoComponent,
  },
  {
    image: '/png/venue-onboarding-2.png',
    descriptionComponent: (
      <Box maxWidth={'80%'} margin={'0 auto'}>
        <Typography fontSize={'16px'} fontFamily={'Poppins'} textAlign={'center'}>
          <b>Start by creating an offer</b> (eg. a free lunch, workout, or service)
        </Typography>
      </Box>
    ),
    videoComponent: <></>,
  },
  {
    image: '/png/venue-onboarding-3.png',
    descriptionComponent: (
      <Typography fontSize={'16px'} fontFamily={'Poppins'} textAlign={'center'}>
        You will be <b>notified</b> when our members reserve
      </Typography>
    ),
    videoComponent: <></>,
  },
  {
    image: '/png/venue-onboarding-4.png',
    descriptionComponent: (
      <Box maxWidth={'90%'} margin={'0 auto'}>
        <Typography fontSize={'16px'} fontFamily={'Poppins'} textAlign={'center'}>
          Our members will show you their <b>reservation ticket</b> when they come to redeem your
          offer
        </Typography>
      </Box>
    ),
    videoComponent: <></>,
  },
  {
    image: '/png/venue-onboarding-5.png',
    descriptionComponent: (
      <Box maxWidth={'90%'} margin={'0 auto'}>
        <Typography fontSize={'16px'} fontFamily={'Poppins'} textAlign={'center'}>
          <b>Members will post on Instagram</b> about their experience within 24-72 hours
        </Typography>
      </Box>
    ),
    videoComponent: <></>,
  },
];

type Props = {
  venue?: Venue;
  handleSkip?: () => void;
  handleLetsStart?: () => void;
} & BaseDialogProps;

export const OnBoardingDialog: React.FC<Props> = ({
  venue,
  handleLetsStart,
  handleSkip,
  ...rest
}) => {
  const [activeStep, setActiveStep] = React.useState(0);

  const handleNextSteps = () => {
    if (activeStep < ONLINE_BRAND_STEPS(venue?.name || '').length - 1) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      handleLetsStart?.();
    }
  };

  const handlePrevSteps = () => {
    if (activeStep > 0) {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    } else {
      handleSkip?.();
    }
  };

  const isOnlineBrand = venue?.tags?.includes(VENUE_MANUAL_TAGS.Online);

  const imageSrc = isOnlineBrand
    ? ONLINE_BRAND_STEPS(venue?.name || '')[activeStep].image
    : VENUE_STEPS(venue?.name || '')[activeStep].image;

  const videoComponent = isOnlineBrand
    ? ONLINE_BRAND_STEPS(venue?.name || '')[activeStep].videoComponent
    : VENUE_STEPS(venue?.name || '')[activeStep].videoComponent;

  return (
    <BaseDialog
      width='650px'
      minWidth='650px'
      height='620px'
      titleComponent={<></>}
      showCloseIcon={false}
      {...rest}
    >
      <Box p='15px'>
        <FlowStepper
          numberOfSteps={
            isOnlineBrand
              ? ONLINE_BRAND_STEPS(venue?.name || '').length
              : VENUE_STEPS(venue?.name || '').length
          }
          activeStep={activeStep}
        />
        <Box display={'flex'} flexDirection={'column'} mt={3}>
          <Box display={'flex'} flexDirection={'column'} gap={'20px'}>
            {imageSrc && (
              <img src={imageSrc} alt={`step-${imageSrc}`} width={'100%'} height={'auto'} />
            )}
            {videoComponent}
            <Box display={'flex'} flexDirection={'column'} gap={'6px'}>
              {isOnlineBrand
                ? ONLINE_BRAND_STEPS(venue?.name || '')[activeStep].descriptionComponent
                : VENUE_STEPS(venue?.name || '')[activeStep].descriptionComponent}
            </Box>
          </Box>
        </Box>
        <Box
          display={'flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
          position={'absolute'}
          bottom={24}
          left={24}
          right={24}
          padding={'20px'}
        >
          <SkipButton onClick={handlePrevSteps}>{activeStep === 0 ? 'Skip' : 'Back'}</SkipButton>
          <HowItWorksButton variant='contained' color='primary' onClick={handleNextSteps}>
            {activeStep === 0 ? 'How it works' : activeStep === 4 ? `Let's start!` : 'Continue'}
          </HowItWorksButton>
        </Box>
      </Box>
    </BaseDialog>
  );
};
